import { Component, computed, effect, Signal, signal, ViewChild } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { ConfirmationDialogComponent } from '@app/components';
import { FeatureFlag } from '@app/enums';
import { RL1Summary } from '@app/models/payroll/rl1-summary.model';
import { TaxForm } from '@app/models/payroll/tax-form.abstract.model';
import { TaxReturnSummary } from '@app/models/payroll/tax-return-summary.abstract.model';
import { TaxReturn } from '@app/models/payroll/tax-return.model';
import { AddAmendedTaxForm } from '@app/modules/forms/payroll/components/add-amended-tax/add-amended-tax.form';
import { TaxFormConsentDialog } from '@app/modules/payroll/components/tax-returns/tax-form-consent-dialog/tax-form-consent-dialog.component';
import { TaxReturnFormsTables } from '@app/modules/payroll/components/tax-returns/tax-return-forms-tables/tax-return-forms-tables.component';
import { PayrollResolver } from '@app/modules/payroll/payroll.resolver';
import { T4Service, TaxReturnService } from '@app/modules/payroll/services';
import { RL1Service } from '@app/modules/payroll/services/rl1.service';
import { AppDatePipe } from '@app/pipes';
import { AuthService, NotifyService } from '@app/services';
import { FeatureService } from '@app/services/feature.service';
import { T4Summary } from '@models/payroll/t4-summary.model';
import { CRA_FILING_DEADLINE_MAP } from '@payroll/constants/tax-dates';
import * as EmployeeTaxForms from '@payroll/services/tables/employee-tax-forms-table/employee-tax-forms.table';
import * as EmployerTaxPackages from '@payroll/services/tables/employer-tax-packages-table/employer-tax-packages.table';
import { EmployerTaxPackagesTable } from '@payroll/services/tables/employer-tax-packages-table/employer-tax-packages.table';
import { Action } from '@table/interfaces/action.interface';
import { MenuColumnEvent } from '@table/interfaces/menu-column-event.interface';
import { getYear } from 'date-fns';
import { from, merge, Subject } from 'rxjs';
import { map } from 'rxjs/operators';

type PackageType = 'T4' | 'RL1';

@Component({
    templateUrl: './tax-return-show.template.html',
    styleUrl: './tax-return.style.scss',
    providers: [AppDatePipe],
})
export class TaxReturnShow {
    @ViewChild('deletePackageConfirmationDialog') deletePackageConfirmationDialog?: ConfirmationDialogComponent;
    @ViewChild(TaxFormConsentDialog) bulkEmailConfirmationDialog?: TaxFormConsentDialog;
    @ViewChild(AddAmendedTaxForm) addAmendedTaxForm?: AddAmendedTaxForm;
    @ViewChild(TaxReturnFormsTables) employeeTaxFormsTables?: TaxReturnFormsTables;

    currentlyOpenDialog?: 'T4_DENTAL_BENEFIT_REMINDER' | 'ADD_AMENDED_TAX_FORM';
    isLoading = false;
    year: number;
    packageTypeToBeDeleted: PackageType | 'ALL' = 'T4';
    packageTypeToBeEmailed: PackageType | 'ALL' = 'T4';

    hasQcPayrollFlag = toSignal(from(this.featureService.has(FeatureFlag.qcPayroll)));
    get taxReturn(): TaxReturn | T4Summary {
        return this._taxReturn();
    }
    /**
     * The initial TaxReturn is retrieved via the PayrollResolver in the route, therefore if we want to reset it we have to emit in the underlying Observable
     */
    set taxReturn(taxReturn: TaxReturn | T4Summary) {
        this._refreshTaxReturn$.next(taxReturn);
    }

    employerTaxPackagesTable = computed(() => {
        const t4Summary = this.t4Summary();
        const rl1Summary = this.rl1Summary();
        const summaries = [];
        if (t4Summary) {
            summaries.push(t4Summary);
            // RL1 Summary is not provided if the feature flag is off
            if (rl1Summary) {
                summaries.push(rl1Summary);
            }
            return new EmployerTaxPackagesTable(summaries);
        }
    });

    t4Summary = signal<T4Summary | undefined>(undefined);
    rl1Summary = signal<RL1Summary | undefined>(undefined);

    /**
     * Returns that have been submitted. Used to hide some forms from the amendment dialog
     */
    completedReturns = computed(() => {
        const completedReturns: PackageType[] = [];
        if (this.t4Summary()?.isComplete) {
            completedReturns.push('T4');
        }
        if (this.rl1Summary()?.isComplete) {
            completedReturns.push('RL1');
        }
        return completedReturns;
    });

    loadSummaries = effect(
        () => {
            if (this.isLegacyT4Summary(this.taxReturn)) {
                // We instantiate a TaxReturn with the year when navigating to this page, except for the legacy page which uses a T4Summary that might not have a year
                if (!this.taxReturn.year) {
                    this.taxReturn.year = this.year;
                }
                this.t4Summary.set(this.taxReturn);
                return;
            }
            this.t4Summary.set(
                this.taxReturn.t4Summary ?? new T4Summary({ year: this.taxReturn.year, taxReturnId: this.taxReturn.id })
            );
            this.rl1Summary.set(
                this.taxReturn.rl1Summary ??
                    new RL1Summary({ year: this.taxReturn.year, taxReturnId: this.taxReturn.id })
            );
        },
        { allowSignalWrites: true }
    );

    /**
     * _refreshTaxReturn$ is merged with the route.data Observable which allows us to refresh the TaxReturn model despite it being pulled from the resolver in the route.
     * Calling next() on this private Subject will act as if a new model was resolved when loading the page.
     */
    private _refreshTaxReturn$ = new Subject<TaxReturn | T4Summary>();
    private _taxReturn: Signal<TaxReturn | T4Summary> = toSignal(
        merge(this.route.data.pipe(map(({ models }) => models.get('taxReturn'))), this._refreshTaxReturn$),
        { requireSync: true }
    );

    constructor(
        private auth: AuthService,
        private notify: NotifyService,
        public router: Router,
        private route: ActivatedRoute,
        private t4Service: T4Service,
        private rl1Service: RL1Service,
        private taxReturnService: TaxReturnService,
        private featureService: FeatureService,
        private resolver: PayrollResolver
    ) {
        this.year = route.snapshot.params.year ?? getYear(new Date()) - 1;
    }

    /**
     * Checks whether we're using the legacy flow that only supports a T4Summary or the new flow which uses the TaxReturn model
     */
    isLegacyT4Summary(model: T4Summary | TaxReturn): model is T4Summary {
        return model instanceof T4Summary;
    }

    closeDialog(): void {
        this.currentlyOpenDialog = undefined;
    }

    get craFilingDeadline(): string | undefined {
        return CRA_FILING_DEADLINE_MAP[this.year];
    }

    handleEmployerTaxPackagesMenuEvent(event: MenuColumnEvent<TaxReturnSummary, EmployerTaxPackages.MenuEvent>): void {
        const actionType = event.data?.isRL1Summary() ? 'RL1' : 'T4';
        switch (event.menuItem.event) {
            case 'view':
                this.router.navigate([actionType, 'submit'], { relativeTo: this.route });
                break;
            case 'download':
                if (event.data) {
                    this.downloadTaxPackage(event.data);
                }
                break;
            case 'delete':
                this.deleteTaxPackage(actionType);
                break;
            case 'preview':
                if (event.data) {
                    this.downloadTaxReturnPreviews([actionType]);
                }
        }
    }

    async deleteTaxPackage(packageTypeToBeDeleted: PackageType | 'ALL'): Promise<void> {
        this.packageTypeToBeDeleted = packageTypeToBeDeleted;
        const confirmed = await this.deletePackageConfirmationDialog?.open();
        if (!confirmed) return;

        try {
            this.isLoading = true;
            if (this.packageTypeToBeDeleted === 'RL1') {
                await this.rl1Summary()?.param('company', this.auth.company.id).delete();
                this.rl1Summary.set(new RL1Summary({ year: this.taxReturn.year, taxReturnId: this.taxReturn.id }));
            } else if (this.packageTypeToBeDeleted === 'T4') {
                await this.t4Summary()?.param('company', this.auth.company.id).delete();
                this.t4Summary.set(new T4Summary({ year: this.taxReturn.year, taxReturnId: this.taxReturn.id }));
            } else if (this.packageTypeToBeDeleted === 'ALL' && !this.isLegacyT4Summary(this.taxReturn)) {
                await this.taxReturnService.deleteTaxReturn(this.taxReturn);
            }
            this.notify.success(`payroll.taxForms.${this.packageTypeToBeDeleted}.deleteDialog.success`);
            if (this.isLegacyT4Summary(this.taxReturn) || this.packageTypeToBeDeleted === 'ALL') {
                this.router.navigate(['/payroll/records/tax-returns', this.t4Summary()?.year]);
            }
        } catch (e) {
            this.notify.error(e);
        } finally {
            this.isLoading = false;
        }
    }

    handleEmployerTaxPackagesAction({ event }: Action): void {
        switch (event as EmployerTaxPackages.ActionEvent) {
            case 'T4_generate':
                this.currentlyOpenDialog = 'T4_DENTAL_BENEFIT_REMINDER';
                break;
            case 'T4_finalize':
                this.router.navigate(['T4', 'submit'], { relativeTo: this.route });
                break;
            case 'RL1_generate':
                this.generateRL1s();
                break;
            case 'RL1_finalize':
                this.router.navigate(['RL1', 'submit'], { relativeTo: this.route });
                break;
        }
    }

    handleEmployeeTaxFormEvent(event: MenuColumnEvent<TaxForm, EmployeeTaxForms.Event>): void {
        if (!event.data?.id) return;

        switch (event.menuItem.event) {
            case 'download':
                this.downloadTaxFormPreview(event.data);
                break;
            case 'view':
                this.router.navigate([
                    '/payroll/records/tax-forms',
                    event.data.isRL1() ? 'rl1-show' : 't4-show',
                    event.data.id,
                ]);
                break;
        }
    }

    async generateT4s(): Promise<void> {
        return this.generateSummary(this.t4Summary());
    }

    async generateRL1s(): Promise<void> {
        return this.generateSummary(this.rl1Summary());
    }

    /**
     * Downloads PDF previews for RL-1 summary, T4 summary or both simultaneously
     */
    async downloadTaxReturnPreviews(summaries: PackageType[]): Promise<void> {
        this.isLoading = true;
        await Promise.allSettled(
            summaries.map((summaryKey) =>
                this.downloadTaxReturnPreview(summaryKey === 'RL1' ? this.rl1Summary() : this.t4Summary())
            )
        );
        this.isLoading = false;
    }

    /**
     * Emails the individual final PDFs from RL-1 summary, T4 summary or both simultaneously
     */
    async sendFormsToEmployees(summaries: PackageType[]): Promise<void> {
        this.packageTypeToBeEmailed = summaries.length === 1 ? summaries[0] : 'ALL';

        if (!(await this.bulkEmailConfirmationDialog?.openDialog())) return;

        this.isLoading = true;
        await Promise.allSettled(
            summaries.map((summaryKey) =>
                this.sendFormToEmployees(summaryKey === 'RL1' ? this.rl1Summary() : this.t4Summary())
            )
        );
        this.isLoading = false;
    }

    /**
     * Downloads individual employee preview for RL-1 or T4
     */
    async downloadTaxFormPreview(taxReturn: TaxForm): Promise<void> {
        this.isLoading = true;
        try {
            await this.taxReturnService.downloadTaxFormPreview(taxReturn);
        } catch (e) {
            this.notify.error(e);
        } finally {
            this.isLoading = false;
        }
    }

    /**
     * Downloads ZIP of all final employee PDFs for RL-1 summary or T4 summary
     */
    async downloadTaxPackage(summary: TaxReturnSummary): Promise<void> {
        this.isLoading = true;
        try {
            await this.taxReturnService.downloadTaxPackage(summary);
        } catch (e) {
            this.notify.error(e);
        } finally {
            this.isLoading = false;
        }
    }

    async handleCreateAmendment(): Promise<void> {
        this.isLoading = true;

        try {
            let id: string;
            if (this.hasQcPayrollFlag()) {
                id = (
                    await this.taxReturnService
                        .generateTaxReturn(new TaxReturn({ year: this.year, isAmendment: true }))
                        .toPromise()
                ).id;
            } else {
                id = (await this.t4Service.generateT4Summary(new T4Summary({ year: this.year }))).id.toString();
            }
            this.router.navigate(['..', id], { relativeTo: this.route });
        } catch (e) {
            this.notify.error(e);
        } finally {
            this.isLoading = false;
        }
    }

    /**
     * Creates an amended tax slip for a specific employee
     */
    async addAmendedTaxSlip(form?: AddAmendedTaxForm): Promise<void> {
        const prEmployeeId = form?.addEmployee()?.prEmployeeId;
        if (!prEmployeeId) {
            return;
        }

        form.submit();

        if (!form.valid) {
            return;
        }

        this.closeDialog();

        this.isLoading = true;

        const amendments: Promise<void>[] = [];

        if (this.hasQcPayrollFlag()) {
            // When the RL1 feature is enabled, they can add an employee to a T4, RL-1 or both
            if (form.packages.T4.isSelected()) {
                amendments.push(this.addAmendedT4(prEmployeeId));
            }
            if (form.packages.RL1.isSelected()) {
                amendments.push(this.addAmendedRL1(prEmployeeId));
            }
        } else {
            // When the RL1 feature is disabled, they can only add an employee to a T4
            amendments.push(this.addAmendedT4(prEmployeeId));
        }

        Promise.allSettled(amendments).finally(() => {
            // Adding an amended employee tax slip can cause updates to the summary status on the BE that can only be reflected in the FE if we re-retrieve the base Tax Return
            // ie. (the "canSubmit" is toggled depending on if there are un-edited employee slips in an amended return)
            this.reloadTaxReturn();
        });
    }

    private async addAmendedT4(prEmployeeId: number): Promise<void> {
        const t4Summary = this.t4Summary();
        if (!t4Summary) {
            return;
        }

        try {
            await this.t4Service.addAmendedT4(t4Summary.year, prEmployeeId);

            this.notify.success('payroll.taxReturns.amendment.T4.created');
        } catch (e) {
            this.notify.error(e);
            throw e;
        }
    }

    private async addAmendedRL1(prEmployeeId: number): Promise<void> {
        const rl1Summary = this.rl1Summary();
        if (!rl1Summary) {
            return;
        }

        try {
            await this.rl1Service.addAmendedRL1(rl1Summary.year, prEmployeeId);
            this.notify.success('payroll.taxReturns.amendment.RL1.created');
        } catch (e) {
            this.notify.error(e);
            throw e;
        }
    }

    /**
     * Used to reset the Tax Return from the back-end.
     * Mostly used for amendments which make updates to the summaries automatically but don't return the updated summary
     */
    private async reloadTaxReturn(): Promise<void> {
        this.isLoading = true;
        const models = await this.resolver.resolve(this.route.snapshot, this.router.routerState.snapshot);
        this.taxReturn = models.get<TaxReturn | T4Summary>('taxReturn');
        this.isLoading = false;
    }

    private async generateSummary(summary?: TaxReturnSummary): Promise<void> {
        this.isLoading = true;
        try {
            if (!this.isLegacyT4Summary(this.taxReturn) && summary?.isRL1Summary()) {
                const rl1Summary = await this.rl1Service.generateRL1Summary(summary ?? new RL1Summary());
                this.rl1Summary.set(rl1Summary);
                this.employeeTaxFormsTables?.reloadTables('RL1');
            }
            if (summary?.isT4Summary()) {
                const t4Summary = await this.t4Service.generateT4Summary(summary ?? new T4Summary());

                if (this.isLegacyT4Summary(this.taxReturn)) {
                    // Legacy flow changes the URL from 'new' to the id
                    this.router.navigate(['/payroll/records/tax-returns', t4Summary.year, t4Summary.id]);
                } else {
                    // Updated flow stays on the same page throughout
                    this.t4Summary.set(t4Summary);
                    this.employeeTaxFormsTables?.reloadTables('T4');
                }
            }
        } catch (e) {
            this.notify.error(e);
        } finally {
            this.isLoading = false;
        }
    }

    /**
     * Downloads PDF previews for RL-1 summary or T4 summary
     */
    private async downloadTaxReturnPreview(summary?: TaxReturnSummary): Promise<void> {
        if (!summary) {
            return;
        }
        try {
            await this.taxReturnService.downloadTaxReturnPreview(summary);
        } catch (e) {
            this.notify.error(e);
        }
    }

    /**
     * Emails the individual final PDFs from RL-1 summary or T4 summary
     */
    private async sendFormToEmployees(summary?: TaxReturnSummary): Promise<void> {
        if (!summary) {
            return;
        }

        try {
            await this.taxReturnService.sendBulkEmails(summary);
            this.notify.success(`payroll.taxForms.${this.packageTypeToBeEmailed}.emailDialog.success`);
        } catch (e) {
            this.notify.error(e);
        } finally {
            this.isLoading = false;
        }
    }
}
